/**
 * @fileoverview Diagram Breadcrumb Component
 * 
 * Shows navigation path for drill-down navigation between linked diagrams.
 * 
 * @see Issue #789 - Universal Diagram Linking System
 */

import React, { useCallback } from 'react';
import { useNavigationStore } from '../../stores/navigationStore';
import { Button } from '../ui/button';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '../../hooks/useTranslation';
import { getDiagramNameForDisplay } from '../../dashboard/utils/diagramHelpers';

interface DiagramBreadcrumbProps {
  currentDiagramId: string;
  currentDiagramName?: string;
  className?: string;
}

export const DiagramBreadcrumb: React.FC<DiagramBreadcrumbProps> = ({
  currentDiagramName,
  className,
}) => {
  const { breadcrumbStack, isNavigating, setNavigating } = useNavigationStore();
  const { t: tDiagram } = useTranslation('diagram');
  const { t: tDashboard } = useTranslation('dashboard');

  const labelForStoredName = useCallback(
    (raw: string | undefined): string => {
      const s = (raw ?? '').trim();
      if (!s) {
        return tDiagram('header.breadcrumbRoot', { defaultValue: 'Root' }) as string;
      }
      return getDiagramNameForDisplay(s, tDiagram, tDashboard) as string;
    },
    [tDiagram, tDashboard]
  );

  const currentName =
    currentDiagramName && currentDiagramName.trim()
      ? currentDiagramName
      : (tDiagram('diagramLinkPreviewModal.currentDiagramFallback', { defaultValue: 'Current Diagram' }) as string);

  const handleBreadcrumbClick = (index: number) => {
    if (isNavigating) return;

    const item = breadcrumbStack[index];
    if (!item) return;

    setNavigating(true);
    try {
      const nextStack = breadcrumbStack.slice(0, index);
      try {
        sessionStorage.setItem('diagram-nav-stack', JSON.stringify({ targetDiagramId: item.diagramId, breadcrumbStack: nextStack }));
        sessionStorage.removeItem('diagram-nav-last'); // legacy
      } catch {}
      window.location.href = `/f/${item.folderId}/d/${item.diagramId}`;
    } catch (error) {
      console.error('Failed to navigate to breadcrumb:', error);
    } finally {
      setNavigating(false);
    }
  };

  // Don't show breadcrumb if we're at the root
  if (breadcrumbStack.length === 0) {
    return null;
  }

  return (
    <nav
      className={cn('flex items-center gap-1 text-sm', className)}
      aria-label={tDiagram('header.breadcrumbAriaLabel', { defaultValue: 'Diagram navigation breadcrumb' }) as string}
    >
      {/* Home/Root indicator */}
      <Button
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs"
        data-testid="tid.nav.breadcrumb.root"
        onClick={() => handleBreadcrumbClick(0)}
        disabled={isNavigating}
      >
        <Home className="h-3 w-3 mr-1" />
        {labelForStoredName(breadcrumbStack[0]?.diagramName)}
      </Button>

      {/* Breadcrumb items */}
      {breadcrumbStack.slice(1).map((item, index) => {
        const actualIndex = index + 1;
        return (
          <React.Fragment key={item.diagramId}>
            <ChevronRight className="h-3 w-3 text-muted-foreground" />
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs"
              data-testid={`tid.nav.breadcrumb.item.${item.diagramId}`}
              onClick={() => handleBreadcrumbClick(actualIndex)}
              disabled={isNavigating}
            >
              {labelForStoredName(item.diagramName)}
            </Button>
          </React.Fragment>
        );
      })}

      {/* Current diagram */}
      <ChevronRight className="h-3 w-3 text-muted-foreground" />
      <span className="text-xs font-medium text-foreground px-2">
        {currentName}
      </span>

    </nav>
  );
};
